import { Currency } from "@redotech/money/currencies";
import { moneyFormat } from "@redotech/money/util";
import { useLoad } from "@redotech/react-util/load";
import { GidType, toGid } from "@redotech/redo-model/shopify/gid-utils";
import { getProduct, getVariant } from "@redotech/shopify-storefront";
import { ShopifyStorefrontClient } from "@redotech/shopify-storefront/storefront-client";
import { memo, useCallback, useMemo, useRef } from "react";
import { RedoTextInput } from "../../arbiter-components/input/redo-text-input";
import { RedoSpinner } from "../../arbiter-components/loading/redo-spinner";
import { Divider } from "../../divider";
import { Flex } from "../../flex";
import { Text } from "../../text";

interface ProductTileForOrderProps {
  selectedVariantId: string;
  quantity: string;
  storeUrl?: string;
  storefrontAccessToken?: string;
  onVariantChange: (variantId: string) => void;
  onQuantityChange: (quantity: string) => void;
  readOnly?: boolean;
  quantityLabel?: string;
  allowChangeVariant?: boolean;
  maxQuantity?: number;
  showRemovalCalculation?: boolean;
}

export const ProductTileForOrder = memo(function ProductTileForOrder({
  selectedVariantId,
  quantity,
  storeUrl,
  storefrontAccessToken,
  onVariantChange,
  onQuantityChange,
  readOnly = false,
  quantityLabel = "Quantity",
  allowChangeVariant = true,
  maxQuantity,
  showRemovalCalculation = false,
}: ProductTileForOrderProps) {
  // Extract the base variant ID for the initial lookup - only use the first variant ID to avoid reloading
  const initialVariantIdRef = useRef<string | null>(null);
  if (initialVariantIdRef.current === null && selectedVariantId) {
    initialVariantIdRef.current = selectedVariantId.includes(
      "gid://shopify/ProductVariant/",
    )
      ? selectedVariantId.split("/").pop() || selectedVariantId
      : selectedVariantId;
  }

  // Memoize client credentials to prevent unnecessary reloads - use initial variant only
  const clientParams = useMemo(
    () => ({
      baseVariantId: initialVariantIdRef.current,
      storeUrl,
      storefrontAccessToken,
    }),
    [storeUrl, storefrontAccessToken],
  );

  // First, get the product ID from the variant (lightweight call)
  const productIdResult = useLoad(async () => {
    if (
      !clientParams.baseVariantId ||
      !clientParams.storeUrl ||
      !clientParams.storefrontAccessToken
    )
      return null;

    try {
      const client = new ShopifyStorefrontClient(
        clientParams.storeUrl,
        clientParams.storefrontAccessToken,
      );
      const variantGid = toGid(
        GidType.PRODUCT_VARIANT,
        clientParams.baseVariantId,
      );
      const variant = await getVariant(client, variantGid);

      if (!variant?.product?.id) {
        throw new Error(
          `Variant with ID ${clientParams.baseVariantId} not found`,
        );
      }

      return variant.product.id;
    } catch (error) {
      console.error(
        `Error fetching variant ${clientParams.baseVariantId}:`,
        error,
      );
      return null;
    }
  }, [clientParams]);

  // Then get the full product details using the product ID as cache key
  const productResult = useLoad(async () => {
    if (!productIdResult.value || !storeUrl || !storefrontAccessToken)
      return null;

    try {
      const client = new ShopifyStorefrontClient(
        storeUrl,
        storefrontAccessToken,
      );
      const product = await getProduct(client, productIdResult.value);

      if (!product) {
        throw new Error("Product not found");
      }

      return {
        id: productIdResult.value.split("/").pop() || productIdResult.value,
        handle: product.handle || productIdResult.value,
        title: product.title,
        image: product.featuredImage
          ? {
              src: product.featuredImage.url,
              alt: product.featuredImage.altText || product.title,
            }
          : undefined,
        priceRange: {
          minPrice: {
            amount: product.priceRange.minVariantPrice.amount,
            currency: product.priceRange.minVariantPrice.currencyCode,
          },
          maxPrice: {
            amount: product.priceRange.maxVariantPrice.amount,
            currency: product.priceRange.maxVariantPrice.currencyCode,
          },
        },
        variants: product.variants.nodes.map(
          (variant: { id: string; title: any }) => ({
            id: variant.id.split("/").pop() || variant.id,
            title:
              variant.title === "Default Title" ? product.title : variant.title,
          }),
        ),
      };
    } catch (error) {
      console.error(`Error fetching product ${productIdResult.value}:`, error);
      return null;
    }
  }, [productIdResult.value]);

  const priceRangeString = useMemo(() => {
    if (productResult.value?.priceRange) {
      const { priceRange } = productResult.value;
      const minPrice = moneyFormat({
        amount: +priceRange.minPrice.amount,
        currency: priceRange.minPrice.currency as string as Currency,
      });
      const maxPrice = moneyFormat({
        amount: +priceRange.maxPrice.amount,
        currency: priceRange.maxPrice.currency as string as Currency,
      });
      if (minPrice === maxPrice) {
        return minPrice;
      } else {
        return `${minPrice} - ${maxPrice}`;
      }
    }
    return undefined;
  }, [productResult.value]);

  const variants = useMemo(() => {
    return productResult.value?.variants || [];
  }, [productResult.value?.variants]);

  const selectedVariant = useMemo(() => {
    // Extract the numeric ID from the GID if selectedVariantId is a full GID
    const numericVariantId = selectedVariantId.includes(
      "gid://shopify/ProductVariant/",
    )
      ? selectedVariantId.split("/").pop()
      : selectedVariantId;

    return variants.find((variant) => variant.id === numericVariantId);
  }, [variants, selectedVariantId]);

  const handleVariantSelection = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      onVariantChange(event.target.value);
    },
    [onVariantChange],
  );

  if (productResult.pending) {
    return (
      <Flex
        align="center"
        dir="column"
        gap="md"
        style={{
          backgroundColor: "var(--color-background-secondary)",
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--color-border-secondary)",
          padding: "var(--spacing-lg)",
        }}
      >
        <RedoSpinner size="md" />
        <Text fontSize="sm" textColor="secondary">
          Loading product details...
        </Text>
      </Flex>
    );
  }

  if (productResult.error || !productResult.value) {
    return (
      <Flex
        dir="column"
        gap="md"
        style={{
          backgroundColor: "var(--color-background-secondary)",
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--color-border-secondary)",
          padding: "var(--spacing-md)",
        }}
      >
        <Flex align="center" dir="row" justify="space-between">
          <Flex align="center" gap="xs" justify="center">
            <Text fontSize="sm" textColor="secondary">
              Variant ID:
            </Text>
            <Text fontSize="sm" fontWeight="medium">
              {selectedVariantId}
            </Text>
          </Flex>
        </Flex>
        <Divider />
        <Flex align="center" dir="row" justify="space-between">
          <Flex align="center" gap="xs" justify="center">
            <Text fontSize="sm" textColor="secondary">
              Quantity:
            </Text>
            <Text fontSize="lg" fontWeight="semibold" textColor="success">
              {quantity}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  const product = productResult.value;

  return (
    <Flex
      dir="column"
      gap="lg"
      style={{
        backgroundColor: "var(--color-background-secondary)",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--color-border-secondary)",
        padding: "var(--spacing-lg)",
      }}
    >
      <Flex align="center" dir="row" gap="lg">
        {product.image && (
          <Flex
            style={{
              width: "80px",
              height: "80px",
              borderRadius: "var(--radius-sm)",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <img
              alt={product.image.alt}
              src={product.image.src}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Flex>
        )}
        <Flex dir="column" gap="xs" style={{ flex: 1 }}>
          <Text fontSize="md" fontWeight="semibold" textColor="primary">
            {product.title}
          </Text>
          {priceRangeString && (
            <Text fontSize="sm" textColor="secondary">
              {priceRangeString}
            </Text>
          )}
        </Flex>
      </Flex>

      {variants.length > 0 && (
        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="medium" textColor="secondary">
            Variant
          </Text>
          {allowChangeVariant ? (
            <select
              disabled={readOnly}
              name="variantSelector"
              onChange={handleVariantSelection}
              style={{
                padding: "4px 4px",
                borderRadius: "4px",
                border: "1px solid var(--redo-colors-border-border-primary)",
                color: "var(--color-text-primary)",
                fontSize: "14px",
                fontFamily: "inherit",
                width: "100%",
              }}
              value={selectedVariant?.id || ""}
            >
              {variants.map((variant) => (
                <option key={variant.id} value={variant.id}>
                  {variant.title}
                </option>
              ))}
            </select>
          ) : (
            <Text fontSize="sm" textColor="primary">
              {selectedVariant?.title || "Unknown variant"}
            </Text>
          )}
        </Flex>
      )}

      <Flex dir="column" gap="sm">
        {showRemovalCalculation && maxQuantity !== undefined ? (
          <Flex dir="column" gap="md">
            <Flex dir="row" gap="xs" justify="space-between">
              <Text fontSize="sm" fontWeight="medium" textColor="secondary">
                New order total
              </Text>
              <Text fontSize="md" fontWeight="semibold" textColor="primary">
                {Math.max(0, maxQuantity - parseInt(quantity || "0", 10))} /{" "}
                {maxQuantity}
              </Text>
            </Flex>

            <Flex dir="column" gap="xs">
              <Text fontSize="sm" fontWeight="medium" textColor="secondary">
                {quantityLabel}
              </Text>
              <RedoTextInput
                inputTypeHint="number"
                max={maxQuantity}
                min={1}
                setValue={onQuantityChange}
                size="sm"
                state={readOnly ? "readonly" : "default"}
                value={quantity}
              />
            </Flex>
          </Flex>
        ) : (
          <RedoTextInput
            inputTypeHint="number"
            label={quantityLabel}
            min={1}
            setValue={onQuantityChange}
            size="sm"
            state={readOnly ? "readonly" : "default"}
            value={quantity}
          />
        )}
      </Flex>
    </Flex>
  );
});
